'use client';

import { useCallback } from 'react';

import type { MediaSlot } from '@selenos/leads';
import { ChevronDownIcon, MessageCircleIcon } from 'lucide-react';

import { MediaSlot as MediaSlotComponent } from '../../../../_components/media';

interface SpitexHeroProps {
  side: 'supply' | 'demand';
  /**
   * Location phrase including the German preposition, e.g. "in Zürich" or
   * "im Aargau". Provided by ~/lib/spitex-canton-data.ts per canton.
   */
  locationPhrase: string;
  heroSlot: MediaSlot | null;
  whatsappUrl?: string;
}

function getHeroCopy(side: 'supply' | 'demand', locationPhrase: string) {
  return side === 'supply'
    ? {
        headline: `Spitex-Jobs ${locationPhrase}: Ihre Karriere in der Pflege`,
        subheadline: 'Flexible Einsaetze. Faire Bezahlung. Sinnvolle Arbeit.',
        ctaLabel: 'Jetzt bewerben',
        whatsappCtaLabel: 'WhatsApp Bewerbung',
        scrollTarget: 'lead-form',
      }
    : {
        headline: `Spitex Pflege ${locationPhrase} – Kostenlose Beratung`,
        subheadline:
          'Endlich verstehen, was Spitex kostet und was die Krankenkasse zahlt. Kostenlose Beratung, gepruefte Anbieter, persoenliche Begleitung.',
        ctaLabel: 'Jetzt kostenlos anfragen',
        whatsappCtaLabel: 'WhatsApp Anfrage',
        scrollTarget: 'lead-form',
      };
}

export function SpitexHero({
  side,
  locationPhrase,
  heroSlot,
  whatsappUrl,
}: SpitexHeroProps) {
  const copy = getHeroCopy(side, locationPhrase);

  const handleCtaClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      document
        .getElementById(copy.scrollTarget)
        ?.scrollIntoView({ behavior: 'smooth' });
    },
    [copy.scrollTarget],
  );

  return (
    <section className="relative flex min-h-[80svh] flex-col items-center justify-center overflow-hidden px-4 text-center">
      {heroSlot && (
        <MediaSlotComponent
          slot={heroSlot}
          priority
          className="absolute inset-0 h-full w-full object-cover brightness-[0.25]"
        />
      )}

      {/* Dark overlay for text readability */}
      <div className="pointer-events-none absolute inset-0 bg-black/20" />

      <div className="relative z-10 mx-auto max-w-3xl">
        <h1 className="text-3xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
          {copy.headline}
        </h1>

        <p className="mx-auto mt-6 max-w-xl text-lg text-white/80 sm:text-xl">
          {copy.subheadline}
        </p>

        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          {whatsappUrl && (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-14 items-center gap-2 rounded-xl px-8 text-base font-semibold text-white shadow-lg transition-all hover:brightness-110"
              style={{ backgroundColor: '#25D366' }}
              data-test="hero-whatsapp-cta"
            >
              <MessageCircleIcon className="h-5 w-5" />
              {copy.whatsappCtaLabel}
            </a>
          )}
          <a
            href={`#${copy.scrollTarget}`}
            onClick={handleCtaClick}
            className="inline-flex h-14 items-center rounded-xl border border-white/30 bg-white/10 px-8 text-base font-semibold text-white shadow-lg backdrop-blur-sm transition-all hover:bg-white/20"
            data-test="hero-form-cta"
          >
            {copy.ctaLabel}
          </a>
        </div>

        {/* Trust badges */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <span className="inline-flex items-center rounded-full border border-white/20 px-4 py-1.5 text-sm text-white/70">
            Rueckruf innert 1 Stunde
          </span>
          {side === 'supply' ? (
            <span className="inline-flex items-center rounded-full border border-white/20 px-4 py-1.5 text-sm text-white/70">
              Spitex-Organisationen schweizweit
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full border border-white/20 px-4 py-1.5 text-sm text-white/70">
              Nur gepruefte Spitex-Organisationen
            </span>
          )}
          <span className="inline-flex items-center rounded-full border border-white/20 px-4 py-1.5 text-sm text-white/70">
            Datenschutz nach Schweizer Recht (nDSG)
          </span>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDownIcon className="h-6 w-6 text-white/50" />
      </div>
    </section>
  );
}
